import { Box, Grid, Text } from '@chakra-ui/react';

import { FormatType, getFormatter } from '@revelio/replots';

import type {
  CompanyMetrics,
  TrendDirection,
} from '../hooks/useCompetitorComposition';
import { useCurrencyFormatter } from '../hooks/useCurrencyFormatter';
import type { EmployerHealth } from '../hooks/useEmployerHealth';
import { RISK_COLORS } from '../utils/colors';
import type { PercentileSalary } from './useCompanyCompareData';

const pctFormatter = getFormatter(FormatType.PERCENTAGE);
const intFormatter = getFormatter(FormatType.INTEGER);

const TREND_LABELS: Record<TrendDirection, string> = {
  growing: 'Growing',
  stable: 'Stable',
  declining: 'Declining',
};

interface MetricRowProps {
  label: string;
  values: Array<{
    text: string;
    color?: string;
    isBest?: boolean;
    isWorst?: boolean;
  }>;
  colCount: number;
}

const MetricRow = ({ label, values, colCount }: MetricRowProps) => (
  <Grid
    templateColumns={`140px repeat(${colCount}, 1fr)`}
    gap={2}
    py={2}
    borderBottom="1px solid"
    borderColor="gray.50"
  >
    <Text fontSize="xs" color="gray.500" fontWeight="medium" alignSelf="center">
      {label}
    </Text>
    {values.map((v, i) => (
      <Box key={i} textAlign="center">
        <Text
          fontSize="sm"
          fontWeight={v.isBest ? 'bold' : 'normal'}
          color={v.color ?? 'gray.700'}
        >
          {v.text}
          {v.isBest && ' *'}
        </Text>
      </Box>
    ))}
  </Grid>
);

interface CompanyCompareMetricsProps {
  metrics: CompanyMetrics[];
  healthData: EmployerHealth[];
  companyOrder: number[]; // rcid order
  userSalary: number;
  percentileSalaries: PercentileSalary[];
  userPercentile: number | null;
}

export const CompanyCompareMetrics = ({
  metrics,
  healthData,
  companyOrder,
  userSalary,
  percentileSalaries,
  userPercentile,
}: CompanyCompareMetricsProps) => {
  const salaryFormatter = useCurrencyFormatter();
  const metricsMap = new Map(metrics.map((m) => [m.companyId, m]));
  const healthMap = new Map(healthData.map((h) => [h.companyId, h]));
  const percentileMap = new Map(
    percentileSalaries.map((p) => [p.companyId, p.salaryAtPercentile])
  );
  const colCount = companyOrder.length;

  const hasPercentileData =
    userPercentile !== null && percentileSalaries.length > 0;

  const getValues = <T,>(
    extract: (
      m: CompanyMetrics | undefined,
      h: EmployerHealth | undefined
    ) => T | null,
    format: (v: T) => string,
    colorFn?: (v: T) => string,
    higherIsBetter = true
  ) => {
    const raw = companyOrder.map((rcid) => {
      const m = metricsMap.get(rcid);
      const h = healthMap.get(rcid);
      return extract(m, h);
    });

    const numericValues = raw.filter((v): v is T => v !== null);
    let bestIdx = -1;
    let worstIdx = -1;
    if (numericValues.length > 1 && typeof numericValues[0] === 'number') {
      const nums = raw as Array<number | null>;
      const validNums = nums.filter((v): v is number => v !== null);
      if (validNums.length > 0) {
        const bestVal = higherIsBetter
          ? Math.max(...validNums)
          : Math.min(...validNums);
        const worstVal = higherIsBetter
          ? Math.min(...validNums)
          : Math.max(...validNums);
        bestIdx = nums.indexOf(bestVal);
        worstIdx = nums.indexOf(worstVal);
      }
    }

    return raw.map((v, i) => ({
      text: v !== null ? format(v) : '\u2014',
      color: v !== null && colorFn ? colorFn(v) : undefined,
      isBest: i === bestIdx && bestIdx !== worstIdx,
      isWorst: i === worstIdx && bestIdx !== worstIdx,
    }));
  };

  // Build salary row values — use percentile-based salary when available
  const salaryLabel = hasPercentileData
    ? `Salary (${userPercentile}th pctl)`
    : 'Median Salary';

  const salaryValues = (() => {
    const raw = companyOrder.map((rcid) => {
      if (hasPercentileData) {
        return percentileMap.get(rcid) ?? null;
      }
      return metricsMap.get(rcid)?.salary ?? null;
    });

    const validNums = raw.filter((v): v is number => v !== null);
    let bestIdx = -1;
    let worstIdx = -1;
    if (validNums.length > 1) {
      const bestVal = Math.max(...validNums);
      const worstVal = Math.min(...validNums);
      bestIdx = raw.indexOf(bestVal);
      worstIdx = raw.indexOf(worstVal);
    }

    return raw.map((v, i) => {
      if (v === null) {
        return { text: '\u2014' };
      }
      let text = salaryFormatter(v);
      let color = 'gray.700';
      if (userSalary > 0) {
        const uplift = ((v - userSalary) / userSalary) * 100;
        const sign = uplift >= 0 ? '+' : '';
        text = `${salaryFormatter(v)} (${sign}${uplift.toFixed(0)}%)`;
        color = v >= userSalary ? 'green.600' : 'red.600';
      }
      return {
        text,
        color,
        isBest: i === bestIdx && bestIdx !== worstIdx,
        isWorst: i === worstIdx && bestIdx !== worstIdx,
      };
    });
  })();

  return (
    <Box>
      <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
        Key Metrics
      </Text>

      <MetricRow
        label="Headcount"
        values={getValues(
          (m) => m?.headcount ?? null,
          (v) => intFormatter(v as number)
        )}
        colCount={colCount}
      />

      <MetricRow
        label={salaryLabel}
        values={salaryValues}
        colCount={colCount}
      />

      <MetricRow
        label="Growth Rate"
        values={getValues(
          (m) => m?.growthRate ?? null,
          (v) => pctFormatter(v as number),
          (v) => {
            const rate = v as number;
            if (rate > 0) return 'green.500';
            if (rate < 0) return 'red.500';
            return 'gray.500';
          }
        )}
        colCount={colCount}
      />

      <MetricRow
        label="Attrition Rate"
        values={getValues(
          (m) => m?.attritionRate ?? null,
          (v) => pctFormatter(v as number),
          (v) => {
            const rate = v as number;
            if (rate > 0.15) return 'red.500';
            if (rate > 0.1) return 'orange.500';
            return 'green.500';
          },
          false // lower is better
        )}
        colCount={colCount}
      />

      <MetricRow
        label="Hiring Trend"
        values={companyOrder.map((rcid) => {
          const m = metricsMap.get(rcid);
          const trend = m?.hiringTrend ?? 'stable';
          let color = 'gray.500';
          if (trend === 'growing') color = 'green.500';
          else if (trend === 'declining') color = 'red.500';
          return {
            text: TREND_LABELS[trend],
            color,
          };
        })}
        colCount={colCount}
      />

      <MetricRow
        label="Stability Score"
        values={getValues(
          (_m, h) => h?.stabilityScore ?? null,
          (v) => `${v}/100`,
          (v) => {
            const score = v as number;
            if (score >= 65) return 'green.500';
            if (score < 40) return 'red.500';
            return 'orange.500';
          }
        )}
        colCount={colCount}
      />

      <MetricRow
        label="Risk"
        values={companyOrder.map((rcid) => {
          const h = healthMap.get(rcid);
          const risk = h?.risk;
          return {
            text: risk
              ? risk.charAt(0).toUpperCase() + risk.slice(1)
              : '\u2014',
            color: risk ? RISK_COLORS[risk] : undefined,
          };
        })}
        colCount={colCount}
      />
    </Box>
  );
};
